import {
	Avatar,
	avatar,
	btn,
	cards,
	comp,
	Component,
	Config,
	DateTime,
	fieldset,
	h3,
	Notifier,
	p,
	router,
	tbar
} from "@intermesh/goui";
import {CurrentSection} from "./services/CurrentSection.js";
import {ServerApi} from "./util/ServerApi.js";
import {CurrentCourse} from "./services/CurrentCourse.js";
import {CurrentParticipant} from "./services/CurrentParticipant.js";
import {t} from "./util/Translate.js";
import {CourseEntity} from "./data/CoursesStore.js";
import {IfwFormat} from "./util/IfwFormat.js";
import {Attempt} from "./data/ParticipantsStore.js";

export class CourseSelect extends Component {

	private selections: Component;
	private currentActivity: any | undefined;
	private activityHistory: any[] = [];

	constructor() {
		super("section");
		this.itemId = "course-select";
		this.id = "course-select";
		this.cls = "card";
		this.title = t("Select your activity");

		this.items.add(
			comp({},
				cards({
						activeItem: 0
					}, fieldset(
						{},
						comp(
							{
								tagName: "div",
								cls: "ifw-title-cnt"
							}, h3({
								cls: "ifw-title",
								text: this.title
							})),
						this.selections = comp({
							cls: "hbox",
							style: {
								flexWrap: "wrap",
								justifyContent: "space-evenly"
							}
						}),
					),
				)
			),
			tbar({
					cls: "ifw-bbar"
				},
				btn({
					text: t("Back"),
					handler: async () => {
						if (!this.currentActivity) {
							void router.goto("profile");
							return;
						} else if(!this.currentActivity.parentId) {
							this.currentActivity = null;
							this.mask();
							const rootNode = await this.populateRootNode(null);
							this.draw(rootNode);
							this.unmask();
						} else {
							const a = this.activityHistory.find(i =>i.id === this.currentActivity.parentId);;
							if (a) {
								this.selectActivity(a);
							}
						}
					}
				}),
				'->'
			)
		);

		this.on("render", async () => {
			this.mask();
			const rootNode = await this.populateRootNode(null);
			this.draw(rootNode);
			this.unmask();
		});
	}

	private draw(node: any): void {
		this.selections.items.clear();

		// First we try to render activities and their handler
		for (const a of node.children) {
			this.selections.items.add(
				comp({
						cls: "vbox activities",
						style: {
							cursor: "pointer"
						},
						listeners: {
							added: ({target}) => {
								target.el.addEventListener("click", async (_elm) => {
									await this.selectActivity(a);
								});
							}
						}
					},
					this.getAvatarCmp(a),
					comp({},
						p({
								text: a.name,
								cls: "lc-course-selection"
							},
						),
					)
				)
			);
		}

		for (const c of node.courses) {
			this.selections.items.add(
				comp({
						cls: "vbox activities",
						style: {
							cursor: "pointer"
						},
						listeners: {
							added: ({target}) => {
								target.el.addEventListener("click", async (_elm) => {
									const p = {
										returnProperties: CurrentCourse.returnProperties,
										sectionId: null
									};
									const cs = CurrentSection.get();
									if (cs.id) {
										p.sectionId = cs.id;
									}
									const route = ServerApi.url(CurrentCourse.baseRoute + "/" + c.id, p);

									const result = await ServerApi.call(route);
									if (result.data) {
										sessionStorage.removeItem(CurrentParticipant.storageItem);
										await CurrentParticipant.readIf();
										sessionStorage.setItem(CurrentCourse.storageItem, JSON.stringify(result.data));
										void router.goto("course-status");
									} else {
										Notifier.warning(t("Unable to retrieve course"));
									}
								});
							}
						}
					},
					this.getAvatarCmp(c),
					comp({},
						p({
								text: c.name,
								cls: "lc-course-selection"
							},
						),
					),
					comp({
						flex: 1
					}),
					comp({
							tagName: "span",
							cls: "duration"
						},
						comp({
							tagName: "i",
							cls: "icon small",
							html: "timer",
						}),
						comp({
							tagName: "span",
							html: this.getTotalDuration(c)
						}),
						comp({
							tagName: "span",
							html: "&nbsp;(" + t("completed") + ")",
							hidden: !this.hasFinished(c)
						})
					)
				)
			);
		}
	}

	private getAvatarCmp(c: any): Component {
		let avaConfig = {
			title: c.name,
			height: 100,
			width: 100,
			cls: "goui-avatar centered",
		} as Config<Avatar>;
		if (c.image) {
			avaConfig.backgroundImage = ServerApi.thumbUrl(c.image, 100, 100, true);
		} else {
			avaConfig.displayName = c.name;
		}
		return comp({}, avatar(avaConfig));
	}

	private async populateRootNode(id?: string | null) {
		let root: { courses: any[], children: any[] } = {
			courses: [],
			children: []
		};
		const activitiesUrl = ServerApi.url("elearning/activities", {
			returnProperties: 'id,name,className,image,parentId,children[&],languages[*,language],courses[course[id,name,className,image,languages[*,language],sections]]',
			q: [
				['andWhere', {
					'parentId': id
				}]
			],
			limit: 0
		});
		const coursesUrl = ServerApi.url("elearning/frontend/courses", {
			returnProperties: 'id,name,estimatedDuration,className,image,languages[*,language[key]],sections[sectionId],blocks[block]',
			q: [
				['andWhere', {'activities.activityId': id}],
				['joinRelation', 'activities', false, 'LEFT']
			],
			limit: 0
		});
		const activitiesData = await ServerApi.call(activitiesUrl, {}, "GET"),
			coursesData = await ServerApi.call(coursesUrl, {}, "GET");
		root.children = await this.getAvailable(activitiesData.data);
		root.courses = await this.getAvailable(coursesData.data);
		return root;
	}

	private async getAvailable(data: any[]) {
		const cs = await CurrentSection.get();
		let availableItems = data.filter((item: any) => {
			let hasLanguage = false;
			for (let i = 0, l = item.languages.length; i < l; i++) {
				if (item.languages[i].language.key.toUpperCase() === sessionStorage.getItem("language")) {
					hasLanguage = true;
					break;
				}
			}

			if (!hasLanguage) {
				return false;
			}
			//
			if (Object.keys(item).indexOf("sections") === -1) {
				return true; // No sections, so no filtering
			}
			for (let i = 0, l = item.sections.length; i < l; i++) {
				if (item.sections[i].sectionId == cs.id) {
					return true;
				}
			}

			return false;
		});

		availableItems = availableItems.sort((a: CourseEntity, b: CourseEntity) => {
			const nameA = a.name.toUpperCase(); // ignore upper and lowercase
			const nameB = b.name.toUpperCase(); // ignore upper and lowercase
			if (nameA < nameB) {
				return -1;
			}
			if (nameA > nameB) {
				return 1;
			}

			// names must be equal
			return 0;
		});
		return availableItems;
	}

	private async selectActivity(activity: any) {
		if (this.currentActivity) {
			if (!this.activityHistory.find(i => i.id === this.currentActivity.id)) {
				this.activityHistory.push(this.currentActivity);
			}
		}

		this.currentActivity = activity;
		this.mask();
		const rootNode = await this.populateRootNode(activity.id);
		this.draw(rootNode);
		this.unmask();
	}

	private getTotalDuration(item: CourseEntity): string {
		if (!item.estimatedDuration) {
			return t("Unknown");
		}

		const pc = CurrentParticipant.get(), now = new DateTime();
		let m = IfwFormat.ifwDurationToMinutes(item.estimatedDuration);
		for (const b of item.blocks) {
			const blockId = b.blockId;
			let hasCompleted = false;
			for (const a of pc.attempts!) {
				if (a.blockId != blockId) {
					continue;
				}
				hasCompleted = (a.succeeded && now.compare(new DateTime(a.expiresAt)) <= 0);
				if (hasCompleted) {
					break;
				}
			}
			if (hasCompleted && b.block.estimatedDuration) {
				m -= IfwFormat.ifwDurationToMinutes(b.block.estimatedDuration);
			}
		}
		return IfwFormat.ifwDuration("PT" + m + "M");
	}

	private hasFinished(item: CourseEntity, lastAttemptIsLeading ?: boolean): boolean {
		lastAttemptIsLeading = lastAttemptIsLeading || false;

		const pc = CurrentParticipant.get(), now = new DateTime();
		let hasCompleted = true;
		for (const b of item.blocks) {
			let blockCompleted = false;
			const attempts = pc.attempts?.filter((att: Attempt) => {
				return att.blockId == b.blockId;
			})
			for (const a of attempts!) {
				if (!a.succeeded) {
					continue;
				}
				blockCompleted = (now.compare(new DateTime(a.expiresAt)) <= 0);
				if (blockCompleted && !lastAttemptIsLeading) {
					break;
				}
			}
			hasCompleted = hasCompleted && blockCompleted;
		}
		return hasCompleted;
	}
}